import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import domainPricing from "../domainPricing.json";
import { getDomainPricingUrl } from "../config/apiConfig";
import "../css/domain-scoped.css";
import EnhancedSEO from "../Components/EnhancedSEO";
import { 
  generateMetaTags, 
  pageConfigs, 
  generateServiceSchema,
  generateFAQSchema
} from "../utils/seoUtils";

// Icons
import { 
  FaSearch,
  FaGlobe,
  FaShieldAlt,
  FaRocket,
  FaCheck,
  FaArrowRight,
  FaBuilding,
  FaEye,
  FaEnvelope,
  FaBrush,
  FaLock,
  FaStar,
  FaServer,
  FaInfoCircle,
  FaSync
} from "react-icons/fa";

const Domain = () => {
  const [domainName, setDomainName] = useState("");
  const [domainPricingData, setDomainPricingData] = useState([]);
  const [popularDomains, setPopularDomains] = useState([]);
  const [heroDomains, setHeroDomains] = useState([]);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const [priceRefreshInterval, setPriceRefreshInterval] = useState(null);
  const [updateNotification, setUpdateNotification] = useState(null);
  const [forceRerender, setForceRerender] = useState(0); // Force re-render

  // Fetch domain pricing from ClubHosty API with fallback
  const fetchDomainPricing = useCallback(async (isInitialLoad = true, useProxy = false) => {
    try {
      if (!isInitialLoad) {
        setIsPriceLoading(true);
      }
      
      const apiUrl = getDomainPricingUrl(useProxy);
      console.log(`${isInitialLoad ? 'Initial' : 'Refresh'} - Fetching domain pricing from ClubHosty API...`);
      console.log('🌐 Making API call to:', apiUrl, useProxy ? '(via proxy)' : '(direct)');
      
      const response = await axios.get(apiUrl, {
        timeout: 10000,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('API Response:', {
        status: response.status,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataLength: response.data?.length,
        sampleData: response.data?.slice(0, 100) // First 100 characters
      });
      
      // The API returns JavaScript code that writes HTML table, not JSON
      // We need to parse the HTML table from the response
      if (response.data && typeof response.data === 'string') {
        const htmlContent = response.data;
        console.log('HTML content sample:', htmlContent.substring(0, 200));
        
        // Extract the table HTML from the JavaScript
        const tableMatch = htmlContent.match(/<table[^>]*>(.*?)<\/table>/s);
        if (tableMatch) {
          const tableHTML = tableMatch[0];
          console.log('Table HTML found');
          
          // Parse table rows to extract domain data - skip the header row
          const rowMatches = tableHTML.match(/<tr><td>([^<]+)<\/td><td>([^<]+)<\/td><td>([^<]+)<\/td><td>([^<]+)<\/td><td>([^<]+)<\/td><\/tr>/g);
          
          if (rowMatches && rowMatches.length > 0) {
            console.log(`Found ${rowMatches.length} data rows`);
            
            const parsedData = rowMatches.map(row => {
              // Extract cell contents
              const cellMatches = row.match(/<td>([^<]*)<\/td>/g);
              if (cellMatches && cellMatches.length === 5) {
                const cells = cellMatches.map(cell => cell.replace(/<\/?td>/g, ''));
                return {
                  extension: cells[0], // .in, .com, etc.
                  minYears: cells[1],  // 1
                  register: cells[2],  // ₹249.00
                  transfer: cells[3],  // ₹249.00  
                  renew: cells[4]      // ₹816.02
                };
              }
              return null;
            }).filter(item => item !== null);
            
            console.log(`Successfully parsed ${parsedData.length} domain records`);
            console.log('Sample parsed data:', parsedData.slice(0, 3));
            console.log('Full first 5 records:', parsedData.slice(0, 5).map((item, index) => ({
              index,
              extension: item.extension,
              register: item.register,
              transfer: item.transfer,
              renew: item.renew
            })));
            
            if (parsedData.length > 0) {
              console.log('✅ Setting domain pricing data with', parsedData.length, 'records');
              setDomainPricingData(parsedData);
              
              // Extract available TLDs
              const tlds = parsedData
                .map(item => {
                  const extension = item.extension;
                  if (!extension || typeof extension !== 'string') return null;
                  return extension.startsWith('.') ? extension : '.' + extension;
                })
                .filter(tld => tld !== null);
              
              // Force re-render by clearing first, then setting new data
              console.log('🔄 Clearing and re-generating domain data...');
              setPopularDomains([]);
              setHeroDomains([]);
              
              // Use setTimeout to ensure state is cleared before setting new data
              setTimeout(() => {
                // Generate popular domains immediately after API data is loaded
                console.log('BEFORE generatePopularDomains - parsedData:', parsedData.slice(0, 3));
                const popularDomainsData = generatePopularDomains(parsedData);
                console.log('AFTER generatePopularDomains - popularDomainsData:', popularDomainsData);
                setPopularDomains(popularDomainsData);
                console.log('AFTER setPopularDomains called');
                
                // Generate hero domains with API pricing
                console.log('BEFORE generateHeroDomains - parsedData:', parsedData.slice(0, 3));
                const heroDomainsData = generateHeroDomains(parsedData);
                console.log('AFTER generateHeroDomains - heroDomainsData:', heroDomainsData);
                setHeroDomains(heroDomainsData);
                console.log('AFTER setHeroDomains called');
                
                // Update last refresh time
                setLastPriceUpdate(new Date());
                setIsPriceLoading(false);
                
                // Force component re-render
                setForceRerender(prev => prev + 1);
                
                // Show success notification
                if (!isInitialLoad) {
                  setUpdateNotification('Prices updated successfully!');
                  setTimeout(() => setUpdateNotification(null), 3000);
                }
                
                console.log(`${isInitialLoad ? 'Initial load' : 'Refresh'} - Loaded ${parsedData.length} domain pricing records`);
                console.log('Available TLDs:', tlds.slice(0, 10));
                console.log('Popular domains set:', popularDomainsData);
                console.log('Hero domains set:', heroDomainsData);
              }, 100);
              
              return;
            }
          } else {
            console.warn('No table rows found in HTML');
          }
        } else {
          console.warn('No table found in HTML response');
        }
        
        console.log('HTML parsing failed, checking for other formats...');
      }
      
      // Fallback if parsing fails
      console.log('Using local fallback data');
      
      // Only set fallback data on initial load, not on refresh failures
      if (isInitialLoad) {
        setDomainPricingData(domainPricing);
        
        // Set fallback popular domains
        const fallbackPopular = generatePopularDomains(domainPricing);
        setPopularDomains(fallbackPopular);
        
        // Set fallback hero domains
        const fallbackHero = generateHeroDomains(domainPricing);
        setHeroDomains(fallbackHero);
      }
      
      setIsPriceLoading(false);
    } catch (error) {
      console.error(`${isInitialLoad ? 'Initial load' : 'Refresh'} - Error fetching domain pricing:`, error);
      
      // Check if it's a CORS error and we haven't tried proxy yet
      if (!useProxy && process.env.NODE_ENV === 'production' && 
          (error.code === 'ERR_NETWORK' || error.message.includes('CORS') || error.message.includes('Access-Control'))) {
        console.warn('CORS error detected - trying proxy fallback...');
        
        try {
          // Retry with proxy
          return await fetchDomainPricing(isInitialLoad, true);
        } catch (proxyError) {
          console.error('Proxy fallback also failed:', proxyError);
        }
      }
      
      // Handle different error types
      if (error.code === 'ERR_NETWORK' || error.message.includes('CORS') || error.message.includes('Access-Control')) {
        console.warn('CORS error detected - API might not be accessible from live domain');
        
        if (!isInitialLoad) {
          setUpdateNotification('Live pricing unavailable - using cached data');
          setTimeout(() => setUpdateNotification(null), 5000);
        }
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.warn('API timeout - server might be slow');
        
        if (!isInitialLoad) {
          setUpdateNotification('Server timeout - please try again later');
          setTimeout(() => setUpdateNotification(null), 5000);
        }
      } else {
        console.warn('Unknown API error:', error.message);
        
        if (!isInitialLoad) {
          setUpdateNotification('Failed to fetch live prices - using cached data');
          setTimeout(() => setUpdateNotification(null), 5000);
        }
      }
      
      setIsPriceLoading(false);
      
      // Only set fallback data on initial load error, not on refresh errors
      if (isInitialLoad) {
        console.log('Using local fallback data due to API error');
        setDomainPricingData(domainPricing);
        
        // Set fallback popular and hero domains
        const fallbackPopular = generatePopularDomains(domainPricing);
        setPopularDomains(fallbackPopular);
        
        const fallbackHero = generateHeroDomains(domainPricing);
        setHeroDomains(fallbackHero);
      } else {
        console.log('Refresh failed, keeping existing pricing data');
      }
    }
  }, []); // useCallback dependency array

  // Initialize domain pricing and data
  useEffect(() => {
    console.log('🚀 Domain component mounted, starting initialization...');
    
    // Initialize with fallback domains immediately
    console.log('📋 Setting initial fallback domains...');
    const initialPopular = generatePopularDomains([]);
    setPopularDomains(initialPopular);
    
    const initialHero = generateHeroDomains([]);
    setHeroDomains(initialHero);
    
    // Fetch domain pricing data immediately
    console.log('📡 Starting initial API fetch...');
    fetchDomainPricing().then(() => {
      console.log('✅ Initial API fetch completed');
    }).catch((error) => {
      console.error('❌ Initial API fetch failed:', error);
    });
    
    // Set up automatic price refresh every 5 minutes (300,000 ms)
    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing domain pricing...');
      fetchDomainPricing(false); // false = not initial load
    }, 300000); // 5 minutes
    
    setPriceRefreshInterval(interval);
    
    // Cleanup interval on component unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [fetchDomainPricing]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (priceRefreshInterval) {
        clearInterval(priceRefreshInterval);
      }
    };
  }, [priceRefreshInterval]);

  // Manual refresh function for user-triggered updates
  const refreshPricing = async () => {
    console.log('🔄 Manual refresh triggered');
    console.log('Current state before refresh:', {
      domainPricingDataLength: domainPricingData.length,
      popularDomainsLength: popularDomains.length,
      heroDomainsLength: heroDomains.length,
      sampleDomainPricing: domainPricingData.slice(0, 2),
      samplePopular: popularDomains.slice(0, 2),
      sampleHero: heroDomains.slice(0, 2)
    });
    
    // Force complete state clearing
    console.log('🧹 Clearing all state...');
    setDomainPricingData([]);
    setPopularDomains([]);
    setHeroDomains([]);
    
    // Trigger API refresh
    console.log('📡 Fetching fresh data...');
    await fetchDomainPricing(false);
  };

  // Format last update time for display
  const formatLastUpdate = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ${diffMins % 60}m ago`;
    
    return date.toLocaleDateString();
  };

  const submitHandler = (e) => {
    e.preventDefault();
    window.location =
      "https://my.clubhosty.com/cart.php?a=add&domain=register&query=" +
      domainName;
  };

  // Basic domain search functionality only

  // Local domain generation functions removed - keeping only basic search functionality

  // Generate hero domain pricing cards from API data
  const generateHeroDomains = (pricingData) => {
    console.log('generateHeroDomains called with:', {
      dataLength: pricingData?.length || 0,
      isArray: Array.isArray(pricingData),
      sampleData: pricingData?.slice(0, 2) || []
    });
    
    // If API data is not loaded yet, use static fallback
    if (!pricingData || !Array.isArray(pricingData) || pricingData.length === 0) {
      console.log('Using static fallback for hero domains');
      return [
        { ext: ".in", price: "₹169.00", originalPrice: "₹799.00" },
        { ext: ".com", price: "₹499.00", originalPrice: "₹1179.00" },
        { ext: ".online", price: "₹169.00", originalPrice: "₹2055.00" },
        { ext: ".shop", price: "₹89.00", originalPrice: "₹2155.00" },
        { ext: ".org", price: "₹199.00", originalPrice: "₹1199.00" },
        { ext: ".xyz", price: "₹179.00", originalPrice: "₹1299.00" }
      ];
    }

    console.log('Using API data for hero domains generation');

    // Use API data to build hero domains with real pricing
    const heroExtensions = ['.in', '.com', '.online', '.shop', '.org', '.xyz'];
    const heroDomains = [];
    
    heroExtensions.forEach(ext => {
      console.log(`Searching for ${ext} in hero domains...`);
      
      const pricingInfo = pricingData.find(item => {
        const extension = item.extension || item.tld || item.domain;
        if (!extension || typeof extension !== 'string') return false;
        const normalizedExtension = extension.startsWith('.') ? extension : '.' + extension;
        const isMatch = normalizedExtension.toLowerCase() === ext.toLowerCase();
        if (isMatch) {
          console.log(`Found hero match for ${ext}:`, item);
        }
        return isMatch;
      });
      
      if (pricingInfo) {
        console.log(`Adding ${ext} to hero domains:`, pricingInfo);
        const registerPrice = pricingInfo.register || pricingInfo.Register;
        const renewPrice = pricingInfo.renew || pricingInfo.Renew;
        
        // Use register price as current price and renew as original (higher) price
        // If register is higher, switch them
        let currentPrice = registerPrice;
        let originalPrice = renewPrice;
        
        // Parse prices to compare (remove currency symbols and commas)
        const parsePrice = (price) => {
          if (!price || typeof price !== 'string') return 0;
          return parseFloat(price.replace(/[₹,]/g, '')) || 0;
        };
        
        const currentNum = parsePrice(currentPrice);
        const originalNum = parsePrice(originalPrice);
        
        // If register price is higher than renew, swap them for display
        if (currentNum > originalNum && originalNum > 0) {
          currentPrice = renewPrice;
          originalPrice = registerPrice;
        }
        
        heroDomains.push({
          ext: ext,
          price: currentPrice || 'Contact for price',
          originalPrice: originalPrice || currentPrice || 'Contact for price'
        });
      } else {
        // Fallback for missing extensions
        const fallback = {
          '.in': { price: "₹249.00", originalPrice: "₹816.02" },
          '.com': { price: "₹599.91", originalPrice: "₹1,232.63" },
          '.online': { price: "₹245.99", originalPrice: "₹3,962.39" },
          '.shop': { price: "₹188.93", originalPrice: "₹4,183.01" },
          '.org': { price: "₹778.02", originalPrice: "₹1,557.35" },
          '.xyz': { price: "₹314.03", originalPrice: "₹1,518.36" }
        };
        
        if (fallback[ext]) {
          heroDomains.push({
            ext: ext,
            price: fallback[ext].price,
            originalPrice: fallback[ext].originalPrice
          });
        }
      }
    });
    
    return heroDomains.slice(0, 6);
  };

  // Generate popular domains from pricing data
  const generatePopularDomains = (pricingData) => {
    console.log('generatePopularDomains called with:', {
      dataLength: pricingData?.length || 0,
      isArray: Array.isArray(pricingData),
      sampleData: pricingData?.slice(0, 2) || []
    });
    
    if (!pricingData || !Array.isArray(pricingData) || pricingData.length === 0) {
      console.log('No pricing data available, using static fallback');
      return [
        { ext: '.com', register: '₹599.91', renew: '₹1,232.63', transfer: '₹1,167.69' },
        { ext: '.in', register: '₹249.00', renew: '₹816.02', transfer: '₹249.00' },
        { ext: '.org', register: '₹778.02', renew: '₹1,557.35', transfer: '₹1,297.58' },
        { ext: '.net', register: '₹799.00', renew: '₹1,875.00', transfer: '₹1,335.00' },
        { ext: '.xyz', register: '₹314.03', renew: '₹1,518.36', transfer: '₹1,405.81' },
        { ext: '.online', register: '₹245.99', renew: '₹3,962.39', transfer: '₹3,962.39' }
      ];
    }

    console.log('Using API data for popular domains generation');

    // Popular TLDs to prioritize
    const popularTlds = ['.com', '.in', '.org', '.net', '.xyz', '.online', '.co', '.site'];
    const popularDomains = [];
    
    // First, try to get the most popular TLDs
    popularTlds.forEach(tld => {
      console.log(`Searching for ${tld} in pricing data...`);
      
      const pricingInfo = pricingData.find(item => {
        const extension = item.extension || item.tld || item.domain || item.TLD;
        if (!extension || typeof extension !== 'string') return false;
        const normalizedExtension = extension.startsWith('.') ? extension : '.' + extension;
        const isMatch = normalizedExtension.toLowerCase() === tld.toLowerCase();
        if (isMatch) {
          console.log(`Found match for ${tld}:`, item);
        }
        return isMatch;
      });
      
      if (pricingInfo) {
        console.log(`Adding ${tld} to popular domains:`, pricingInfo);
        popularDomains.push({
          ext: tld,
          register: pricingInfo.register || pricingInfo.Register || 'Contact for price',
          renew: pricingInfo.renew || pricingInfo.Renew || 'Contact for price',
          transfer: pricingInfo.transfer || pricingInfo.Transfer || 'Contact for price'
        });
      } else {
        console.log(`No pricing found for ${tld}`);
      }
    });
    
    // If we don't have enough, add more from available data
    if (popularDomains.length < 8) {
      const remaining = pricingData.slice(0, 15); // Check first 15 records
      remaining.forEach(item => {
        const extension = item.extension || item.tld || item.domain || item.TLD;
        if (!extension || typeof extension !== 'string') return;
        const normalizedExtension = extension.startsWith('.') ? extension : '.' + extension;
        
        // Skip if already added
        if (!popularDomains.find(d => d.ext.toLowerCase() === normalizedExtension.toLowerCase())) {
          popularDomains.push({
            ext: normalizedExtension,
            register: item.register || item.Register || 'Contact for price',
            renew: item.renew || item.Renew || 'Contact for price',
            transfer: item.transfer || item.Transfer || 'Contact for price'
          });
        }
      });
    }

    const result = popularDomains.slice(0, 8);
    console.log('Generated popular domains:', result);
    return result;
  };



  const benefits = [
    {
      icon: FaBuilding,
      title: "Represent Your Business",
      description: "Clients will definitely check out your website before reaching to you. Your website representation starts right from its domain name."
    },
    {
      icon: FaEye,
      title: "Get Easily Visible", 
      description: "A clean and easy domain name attracts many visitors. Maximize your online visibility with an easy to recall domain name."
    },
    {
      icon: FaEnvelope,
      title: "Send Professional Emails",
      description: "Create professional email addresses with your domain name to improve credibility and brand recognition."
    },
    {
      icon: FaBrush,
      title: "Establishes Brand Identity",
      description: "Your brand's identity starts with a domain name. Choose from our powerful, memorable names that go perfectly with your audience."
    },
    {
      icon: FaLock,
      title: "Business Safety",
      description: "Protect your business from domain squatting and hijacking. Safeguard your business with copyright and trademark protection."
    },
    {
      icon: FaStar,
      title: "Better Online Exposure",
      description: "A domain name that relates to your business improves your website ranking on search engine pages for better exposure."
    }
  ];

  const steps = [
    {
      icon: FaSearch,
      title: "Select a Domain Name",
      description: "Pick the right domain name extension from a range, including .com, .in, .org and others."
    },
    {
      icon: FaServer,
      title: "Select Hosting Plan",
      description: "Choose the best web hosting price from ClubHosty, that's competitive with the market."
    },
    {
      icon: FaRocket,
      title: "Launch Your Website",
      description: "Setup your website and get the instant account from us to launch your website immediately."
    }
  ];

  // Enhanced SEO configuration
  const domainPageConfig = generateMetaTags(pageConfigs.domain);
  
  // Structured data for domain page
  const domainStructuredData = [
    generateServiceSchema(
      "Domain Registration Services",
      "Professional domain registration services with competitive pricing, free WHOIS privacy, and instant activation for all popular domain extensions."
    ),
    generateFAQSchema([
      {
        question: "How do I search for available domains?",
        answer: "Use our domain search tool above to check if your desired domain name is available. Simply enter the name and select your preferred extension to check availability instantly."
      },
      {
        question: "Do you provide free WHOIS privacy protection?",
        answer: "Yes, we provide free WHOIS privacy protection with domain registration to keep your personal information safe from spam and identity theft."
      },
      {
        question: "Can I transfer my existing domain to ClubHosty?",
        answer: "Yes, you can easily transfer your existing domains to ClubHosty. We provide free domain transfers with competitive renewal pricing and excellent support."
      },
      {
        question: "How long does domain registration take?",
        answer: "Domain registration is instant! Once payment is confirmed, your domain will be activated immediately and you can start using it right away."
      }
    ])
  ];

  return (
    <div className="domain-page-wrapper">
      {/* Enhanced SEO */}
      <EnhancedSEO 
        pageConfig={domainPageConfig}
        structuredData={domainStructuredData}
      />
      
      {/* Price Update Notification */}
      {updateNotification && (
        <div className="price-update-notification">
          <FaCheck className="notification-icon" />
          {updateNotification}
        </div>
      )}

      {/* Hero Section */}
      <section className="domain-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              Search for a domain name
            </h1>
            <p className="hero-description">
              Find and register the perfect domain name for your website. 
              Search from thousands of available domains at competitive prices.
            </p>
            
            {/* Domain Search Form */}
            <form className="domain-search-form" onSubmit={submitHandler}>
              <div className="search-input-group">
                <input
                  type="search"
                  name="search"
                  placeholder="Type in that perfect domain name"
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  required
                />
                <button type="submit" className="search-btn">
                  Search
                </button>
              </div>
            </form>

            {/* Hero Domain Pricing Cards */}
            <div key={`hero-domains-${forceRerender}`}>
              <div className="hero-domain-cards">
                {heroDomains.map((domain, index) => (
                  <div key={`${domain.ext}-${index}-${forceRerender}`} className={`hero-domain-card ${isPriceLoading ? 'loading' : ''}`}>
                    {isPriceLoading && (
                      <div className="price-loading-overlay">
                        <FaSync className="spinning" />
                      </div>
                    )}
                    <div className="info-icon">
                      <FaInfoCircle />
                    </div>
                    <div className="card-extension">{domain.ext}</div>
                    <div className="card-original-price">{domain.originalPrice}</div>
                    <div className="card-price">{domain.price}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* WHOIS Privacy Notice */}
            <div className="whois-notice">
              <span>Free WHOIS privacy protection is included with every eligible domain registration.</span>
              <div className="info-icon">
                <FaInfoCircle />
              </div>
            </div>
          </div>
        </div>
      </section>

      <main>
        {/* Popular Domains Section */}
        <section className="popular-domains-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">Popular Domains</div>
              <h2>Domain Names - Register, Renew & Transfer!</h2>
              <p>
                Choose the perfect domain name for your business and stand out online 
                with our competitive pricing and reliable service.
              </p>
              
              {/* Price Update Status and Refresh Button */}
              <div className="price-update-status">
                <div className="price-info">
                  <span className="update-text">
                    Prices last updated: {formatLastUpdate(lastPriceUpdate)}
                  </span>
                  {isPriceLoading && (
                    <span className="loading-indicator">
                      <FaSync className="spinning" /> Updating...
                    </span>
                  )}
                </div>
                <button 
                  className="refresh-prices-btn"
                  onClick={refreshPricing}
                  disabled={isPriceLoading}
                  title="Refresh domain pricing from ClubHosty API"
                >
                  <FaSync className={isPriceLoading ? 'spinning' : ''} />
                  {isPriceLoading ? 'Updating...' : 'Refresh Prices'}
                </button>
              </div>
            </div>
            
            <div className="row" key={`popular-domains-${forceRerender}`}>
              {popularDomains.length > 0 ? (
                popularDomains.map((domain, index) => (
                  <div key={`${domain.ext}-${index}-${forceRerender}`} className="col-lg-3 col-md-6 mb-4">
                    <div className="domain-card">
                      <div className="domain-extension">{domain.ext}</div>
                      <div className="domain-pricing">
                        <div className="price-item">
                          <span className="price-label">Register:</span>
                          <span className="price-value">{domain.register}</span>
                        </div>
                        <div className="price-item">
                          <span className="price-label">Renew:</span>
                          <span className="price-value">{domain.renew}</span>
                        </div>
                      </div>
                      <a
                        href="https://my.clubhosty.com/cart.php?a=add&domain=register"
                        className="add-to-cart-btn"
                      >
                        Add To Cart
                      </a>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center">
                  <div className="loading-message">
                    Loading domain pricing...
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">Why Choose Domain</div>
              <h2>Explore the Perks of Owning a Domain Name</h2>
              <p>
                Discover how the right domain name can transform your online presence 
                and boost your business credibility.
              </p>
            </div>
            
            <div className="row">
              {benefits.map((benefit, index) => (
                <div key={index} className="col-lg-4 col-md-6 mb-4">
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <benefit.icon />
                    </div>
                    <h4>{benefit.title}</h4>
                    <p>{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Transfer Section */}
        <section className="transfer-section">
          <div className="container">
            <div className="transfer-content">
              <h2>Already have the perfect domain name? <br />That's great!</h2>
              <p>
                We won't mind even if you have a registered domain name, you still can 
                switch your hosting to ClubHosty. We are always happy to add more 
                accounts in our web hosting family.
              </p>
              
              {/* Transfer Restriction Notice */}
              <div className="transfer-notice" style={{
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "8px",
                padding: "15px",
                margin: "20px 0",
                fontSize: "14px",
                color: "#856404"
              }}>
                <strong>⚠️ Important Notice:</strong> Domain transfers are subject to billing verification. 
                Domains with generated invoices (paid or unpaid) require a 60-day waiting period after 
                payment clearance before transfer eligibility. Please ensure all billing obligations are 
                satisfied before initiating a transfer request.
              </div>
              
              <a
                className="transfer-btn"
                href="https://my.clubhosty.com/cart.php?a=add&domain=transfer"
              >
                <span>Transfer Now</span>
                <FaArrowRight />
              </a>
            </div>
          </div>
        </section>

        {/* Steps Section */}
        <section className="steps-section">
          <div className="container">
            <div className="row align-items-center">
              {/* Left Side - Steps */}
              <div className="col-lg-6">
                <div className="steps-content">
                  <div className="section-header text-left">
                    <div className="section-badge">Simple & Fast</div>
                    <h2>Get Your Business Online in 3 Easy Steps</h2>
                  </div>
                  
                  <div className="steps-list">
                    <div className="steps-label">Steps to follow</div>
                    
                    {steps.map((step, index) => (
                      <div key={index} className="step-item">
                        <div className="step-number-small">{index + 1}.</div>
                        <div className="step-content">
                          <h4>{step.title}</h4>
                          <p>{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Right Side - Enhanced Visual Mockup */}
              <div className="col-lg-6">
                <div className="steps-visual">
                  <div className="mockup-container">
                    {/* Browser Frame */}
                    <div className="mockup-frame">
                      <div className="mockup-header">
                        <div className="mockup-controls">
                          <span className="control-dot red pulse"></span>
                          <span className="control-dot yellow pulse-delay-1"></span>
                          <span className="control-dot green pulse-delay-2"></span>
                        </div>
                        <div className="mockup-title">
                          <FaGlobe className="globe-icon" />
                          ClubHosty Dashboard
                        </div>
                        <div className="mockup-url">
                          <FaLock className="lock-icon" />
                          clubhosty.com/dashboard
                        </div>
                      </div>
                      
                      <div className="mockup-content">
                        {/* Step 1 - Domain Selection */}
                        <div className="mockup-step active step-1">
                          <div className="step-badge">
                            <FaSearch className="step-icon" />
                            1. Choose Your Domain
                          </div>
                          <div className="domain-input-section">
                            <div className="domain-input-wrapper">
                              <div className="domain-input">
                                <span className="www-prefix">www.</span>
                                <span className="domain-text typing-animation">yourbusiness</span>
                                <span className="domain-ext">.com</span>
                                <div className="cursor blinking">|</div>
                              </div>
                              <button className="search-btn-mini">
                                <FaSearch />
                              </button>
                            </div>
                            <div className="domain-suggestions">
                              <div className="suggestion available">
                                <FaCheck className="check-icon" />
                                yourbusiness.com - Available
                              </div>
                              <div className="suggestion">
                                yourbusiness.in - ₹249/yr
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Step 2 - Hosting Plan */}
                        <div className="mockup-step step-2">
                          <div className="step-badge">
                            <FaServer className="step-icon" />
                            2. Select Hosting Plan
                          </div>
                          <div className="hosting-cards">
                            <div className="hosting-card">
                              <div className="plan-header">
                                <div className="plan-name">Starter</div>
                                <div className="plan-badge">Popular</div>
                              </div>
                              <div className="plan-price">₹99<span>/mo</span></div>
                              <div className="plan-features">
                                <div className="feature">
                                  <FaCheck className="feature-check" />
                                  5GB Storage
                                </div>
                                <div className="feature">
                                  <FaCheck className="feature-check" />
                                  Free SSL
                                </div>
                              </div>
                            </div>
                            <div className="hosting-card selected">
                              <div className="selection-indicator">
                                <FaCheck className="selection-check" />
                              </div>
                              <div className="plan-header">
                                <div className="plan-name">Pro</div>
                                <div className="plan-badge recommended">Recommended</div>
                              </div>
                              <div className="plan-price">₹199<span>/mo</span></div>
                              <div className="plan-features">
                                <div className="feature">
                                  <FaCheck className="feature-check" />
                                  25GB Storage
                                </div>
                                <div className="feature">
                                  <FaCheck className="feature-check" />
                                  Free SSL + CDN
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Step 3 - Launch Website */}
                        <div className="mockup-step step-3">
                          <div className="step-badge">
                            <FaRocket className="step-icon" />
                            3. Launch Your Website
                          </div>
                          <div className="launch-status">
                            <div className="launch-progress">
                              <div className="progress-bar">
                                <div className="progress-fill"></div>
                              </div>
                              <div className="progress-text">Setting up your site...</div>
                            </div>
                            <div className="status-final">
                              <div className="status-icon-wrapper">
                                <FaRocket className="status-icon rocket-launch" />
                                <div className="rocket-trail"></div>
                              </div>
                              <div className="status-content">
                                <div className="status-text">🎉 Ready to Launch!</div>
                                <div className="status-subtext">Your website is ready at yourbusiness.com</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Floating Elements */}
                        <div className="floating-elements">
                          <div className="floating-element element-1">
                            <FaShieldAlt />
                          </div>
                          <div className="floating-element element-2">
                            <FaGlobe />
                          </div>
                          <div className="floating-element element-3">
                            <FaStar />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Background Decorations */}
                    <div className="mockup-background">
                      <div className="bg-circle circle-1"></div>
                      <div className="bg-circle circle-2"></div>
                      <div className="bg-circle circle-3"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Domain;
